import "@configs/OpenAPIClient";
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  split
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { PageWrapper } from "@components/page-wrapper";
import { queryClient } from "@configs/Queries";
import { persistor, store } from "@configs/Redux";
import { getURL } from "@utils/Common";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient, ClientOptions } from "graphql-ws";

import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { LayerProvider } from "@components/common/layer/LayerProvider";
import { StrictMode, type ReactNode } from "react";
import { env, isKeycloakEnabled } from "@configs/Env";
import { keycloak } from "@auth/keycloak";
import { KeycloakProvider } from "@auth/KeycloakProvider";
import { AuthGate } from "@auth/AuthGate";
import { onKeycloakEvent, onKeycloakTokens } from "@auth/authEvents";
import {
  canAttemptRequestRefresh,
  getAccessToken,
  isAccessTokenNearExpiry,
  refreshAuthToken,
  setAccessToken
} from "@auth/tokenManager";
import reportWebVitals from "./reportWebVitals";

async function resolveApolloAuthToken(): Promise<string | null> {
  if (!isKeycloakEnabled()) return null;

  let token = getAccessToken();

  if (!token && keycloak.authenticated && keycloak.token) {
    token = keycloak.token;
    setAccessToken(token, "apollo.authLink.sync");
  }

  if (
    keycloak.authenticated &&
    isAccessTokenNearExpiry() &&
    canAttemptRequestRefresh()
  ) {
    try {
      token = await refreshAuthToken();
    } catch {
      token = getAccessToken();
    }
  }

  return token;
}

const main = async () => {
  /**
   * @fixme TEMP 검증 기간동안 production 모드에서 데이터 API는 사용함.
   */
  if (process.env.NODE_ENV.match(/(production)/g)) {
    // const { worker } = await import("./mocks/browser");
    // await worker.start();
  }

  if (
    process.env.NODE_ENV.match(/(development|test)/g) &&
    JSON.parse(process.env.REACT_APP_USED_MSW ?? "false")
  ) {
    const { worker } = await import("./mocks/browser");
    await worker.start({ onUnhandledRequest: "bypass" });
  }

  // TODO apollo v2 connect
  const httpLink = new HttpLink({
    uri: `https://api.${getURL()}/graphql`,
    credentials: "include"
  });

  const authLink = setContext(async (_, { headers }) => {
    const token = await resolveApolloAuthToken();

    return {
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    };
  });

  const options: ClientOptions = {
    url: `wss://api.${getURL()}/graphql`,
    connectionParams: async () => {
      const token = await resolveApolloAuthToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    shouldRetry: () => true,
    retryAttempts: Infinity,
    keepAlive: 15_000
  };

  const wsLink = new GraphQLWsLink(createClient(options));

  const link = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    wsLink as any,
    authLink.concat(httpLink)
  );

  const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        fetchPolicy: "network-only"
      },
      watchQuery: {
        fetchPolicy: "network-only"
      }
    }
  });

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );

  const appTree: ReactNode = (
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <ApolloProvider client={client}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <LayerProvider>
                <PageWrapper />
              </LayerProvider>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ApolloProvider>
      </PersistGate>
    </Provider>
  );

  if (isKeycloakEnabled()) {
    // ⚠️ Keycloak 모드에서는 StrictMode 사용 금지 (keycloak.init() 이중 호출 방지).
    const appOrigin = env.keycloakLoginRedirectUrl;

    if (window.location.hostname === "127.0.0.1") {
      // eslint-disable-next-line no-console
      console.warn(
        "[keycloak] 127.0.0.1 은 redirect URI 와 불일치합니다. http://localhost:3000 으로 접속하세요."
      );
    }

    // PUBLIC_URL 이 설정된 경우에도 올바른 경로를 생성한다 (중복 슬래시 제거).
    const silentCheckSsoRedirectUri = `${appOrigin}${
      process.env.PUBLIC_URL ?? ""
    }/silent-check-sso.html`.replace(/([^:]\/)\/+/g, "$1");

    root.render(
      <KeycloakProvider
        authClient={keycloak}
        onEvent={onKeycloakEvent}
        onTokens={onKeycloakTokens}
        initOptions={{
          onLoad: "check-sso",
          silentCheckSsoRedirectUri,
          checkLoginIframe: false,
          pkceMethod: "S256"
        }}
      >
        <AuthGate>{appTree}</AuthGate>
      </KeycloakProvider>
    );
  } else {
    // Local/기존 쿠키 모드: Keycloak 없이 렌더 (StrictMode 유지).
    root.render(<StrictMode>{appTree}</StrictMode>);
  }
};

main();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
